<script setup lang="ts">
import { computed, ref, watch } from "vue"

definePage({
  meta: {
    action: 'manage',
    subject: 'TCA',
  },
})

interface Props {
  status: string,
  isRed?: string[],
  isGreen?: string[],
  isPrimary?: string[],
  isWarning?: string[],
  isInfo?: string[],
  isSecondary?: string[],
  isPurple?: string[],
  isTeal?: string[],
  isIndigo?: string[],
  sharedTableId: string,
  btnClass?: string,
  fontClass?: string,
  statusConfig?: any[],
}

const props = withDefaults(defineProps<Props>(), {
  status: '',
  isRed: () => [],
  isGreen: () => [],
  isPrimary: () => [],
  isWarning: () => [],
  isInfo: () => [],
  isSecondary: () => [],
  isPurple: () => [],
  isTeal: () => [],
  isIndigo: () => [],
  sharedTableId: '',
  btnClass: 'large',
  fontClass: '16px',
  statusConfig: () => [],
})

const getStatus = async () => {
  if (!props.sharedTableId) return []
  try {
    const { data: getStatusData } = await useApi<any>(createUrl('/q/de/select/select/select/deSelect', {
      query: {
        tabId: props.sharedTableId
      },
    }))
    return getStatusData.value || []
  } catch (e) {
    console.error(`[Status] Failed to fetch code table for ${props.sharedTableId}:`, e)
    return []
  }
}

const getDataForStatus = ref<any[]>([])

const statusData = computed(() => {
  return (props.statusConfig && props.statusConfig.length > 0) ? props.statusConfig : getDataForStatus.value
})

const nowStatus = computed(() => {
  const s = String(props.status || '').trim().toLowerCase()
  if (!s) return null
  
  // 優先比對 value，再比對 label
  const found = statusData.value.find(item => 
    String(item.value || '').trim().toLowerCase() === s || 
    String(item.label || '').trim().toLowerCase() === s
  )
  
  return found
})

const checkMatch = (list: string[], v: string, l: string) => {
  if (!Array.isArray(list)) return false
  const tv = v.toLowerCase()
  const tl = l.toLowerCase()
  return list.some(i => {
    const criteria = String(i || '').trim().toLowerCase()
    return tv === criteria || tl === criteria
  })
}

const classRef = computed(() => {
  const val = nowStatus.value ? String(nowStatus.value.value || '').trim() : String(props.status || '').trim()
  const label = nowStatus.value ? String(nowStatus.value.label || '').trim() : ''
  
  if (!val && !label) return 'default'
  
  // 優先檢查 Secondary (用於處理發佈/結案等狀態)
  if (['published', 'selectedforteam', 'archive', 'closed', 'announced', 'training'].some(s => s === val.toLowerCase() || s === label.toLowerCase())) return 'secondary'

  if (checkMatch(props.isTeal, val, label)) return 'teal'
  if (checkMatch(props.isIndigo, val, label)) return 'indigo'
  if (checkMatch(props.isRed, val, label)) return 'error'
  if (checkMatch(props.isGreen, val, label)) return 'success'
  if (checkMatch(props.isPrimary, val, label)) return 'primary'
  if (checkMatch(props.isWarning, val, label)) return 'warning'
  if (checkMatch(props.isInfo, val, label)) return 'info'
  if (checkMatch(props.isSecondary, val, label)) return 'secondary'
  if (checkMatch(props.isPurple, val, label)) return 'purple'
  
  return 'primary'
})


// 計算屬性確定顯示文字
const displayLabel = computed(() => {
  const label = nowStatus.value?.label || props.status
  const value = nowStatus.value?.value || props.status
  return label
})

const iconRef = computed(() => {
  switch (classRef.value) {
    case 'success': return 'tabler-circle-check'
    case 'error': return 'tabler-circle-x'
    case 'warning': return 'tabler-alert-triangle'
    case 'info': return 'tabler-info-circle'
    case 'primary': return 'tabler-player-play'
    case 'secondary': return 'tabler-archive'
    case 'purple': return 'tabler-clock-exclamation'
    case 'teal': return 'tabler-currency-dollar'
    case 'indigo': return 'tabler-scale'
    default: return 'tabler-help'
  }
})

watch(() => props.sharedTableId, async (newId) => {
  if (newId && (!props.statusConfig || props.statusConfig.length === 0)) {
    getDataForStatus.value = await getStatus()
  }
}, { immediate: true })

watch(() => props.statusConfig, (newVal) => {
  if (newVal && newVal.length > 0) {
    getDataForStatus.value = [...newVal]
  }
}, { immediate: true })

// 計算屬性推導前一個狀態文字
const previousStatusLabel = computed(() => {
  const currentCode = (nowStatus.value?.value || props.status || '').toString().toLowerCase()
  
  // 以「原始代碼」定義關聯，避免硬編碼中文標籤
  const codeHistoryMap: Record<string, string> = {
    'submitted': 'draft',
    'resubmitted': props.sharedTableId === 'ExpenseChange' ? 'draft' : 'submitted',
    'mpassed': props.sharedTableId === 'ExpenseChange' ? 'draft' : 'submitted',
    'reviewing': 'mpassed',
    'returnedforrevision': 'submitted',
    'fpassed': 'reviewing',
    'correctafterapprove': 'sendaftercorrect',
    'mapproved': 'submitted',
    'fapproved': 'reviewing',
    'sendaftercorrect': 'reviewing',
    'freturnedforrevision': 'reviewing',
    'mreturnedforrevision':'submitted'
  }
  
  const prevCode = codeHistoryMap[currentCode]
  if (!prevCode) return ''
  
  // 查找上一關代碼在代碼表 (statusData) 中的 Label
  const prevItem = statusData.value.find(item => 
    String(item.value || '').trim().toLowerCase() === prevCode.toLowerCase()
  )
  
  return prevItem?.label || prevCode
})

</script>

<template>
  <div class="status-container d-inline-flex flex-column align-center">
    <!-- 上一個狀態顯示區域 -->
    <template v-if="previousStatusLabel">
      <span class="prev-status-text text-caption mb-n1">{{ previousStatusLabel }}</span>
      <v-icon icon="tabler-caret-down-filled" size="12" class="prev-status-icon mb-1"></v-icon>
    </template>

    <v-chip 
      v-if="props.status" 
      :key="String(props.status) + classRef + displayLabel"
      :color="classRef" 
      class="rounded-pill font-weight-bold status-chip" 
      density="comfortable" 
      variant="tonal" 
      :size="btnClass" 
      :style="{ fontSize: fontClass, whiteSpace: 'nowrap', minWidth: '94px', justifyContent: 'center' }"
    >
      <template #prepend>
        <v-icon :icon="iconRef" size="18" class="mr-1"></v-icon>
      </template>
      {{ displayLabel }}
    </v-chip>
  </div>
</template>

<style scoped>
.status-container {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 4px;
}

.status-container:hover {
  transform: scale(1.2);
}

.status-container:hover .status-chip {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
}

/* 當滑鼠移到上方文字區域的情境 */
.prev-status-text:hover {
  transform: scale(1.3);
  font-weight: bold;
  color: inherit;
  opacity: 1;
}

.prev-status-text:hover ~ .status-chip {
  transform: scale(0.85);
  opacity: 0.5;
  filter: blur(0.5px);
}

/* 當滑鼠移到下方 Chip 的情境 */
.status-chip:hover {
  transform: scale(1.1);
}

.status-chip:hover ~ .prev-status-text,
.status-chip:hover ~ .prev-status-icon {
  transform: scale(0.9);
  opacity: 0.4;
}

.status-chip {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  letter-spacing: 0.5px;
}

.prev-status-text {
  display: inline-block; /* 確保 transform 生效 */
  color: #a0a0a0;
  letter-spacing: 1px;
  font-weight: 500;
  opacity: 0.8;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.prev-status-icon {
  color: #c0c0c0;
  opacity: 0.6;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.status-container:hover .prev-status-text,
.status-container:hover .prev-status-icon {
  opacity: 1;
  color: inherit; /* 稍微跟隨主色調感，或保持原樣 */
}
</style>
